import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-hot-toast';
import {
  ERC20_ABI,
  SWAPPER_V2_ABI,
  encodeV3Path,
} from '../contracts/abisV2';
import { CONTRACTS, TOKENS } from '../config/constantsV2';
import { parseTokenAmount, calculateSlippage, getTransactionUrl } from '../utils/helpers';
import web3Provider from '../utils/web3Provider';

export const useSwapperV2 = () => {
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapError, setSwapError] = useState(null);

  const executeCrossDexSwap = async (signer, routeData, fromToken, toToken, fromAmount, slippage, account) => {
    // Atomic cross-DEX swap - single transaction with fee collection
    console.log('🔀 Starting atomic cross-DEX swap:', {
      path: routeData.path,
      dex1: routeData.dex1.name,
      dex2: routeData.dex2.name,
      fromToken: fromToken.symbol,
      toToken: toToken.symbol,
      amount: fromAmount,
    });
    
    const swapperV2 = new ethers.Contract(CONTRACTS.SWAPPER_V2, SWAPPER_V2_ABI, signer);
    const amountInWei = parseTokenAmount(fromAmount, fromToken.decimals);
    const deadline = Math.floor(Date.now() / 1000) + (20 * 60);
    
    // Paths for both swaps
    const path1 = [routeData.path[0], routeData.path[1]]; // fromToken → WFTM
    const path2 = [routeData.path[1], routeData.path[2]]; // WFTM → toToken
    
    // Use lenient slippage for intermediate swap
    const intermediateSlippage = 5;
    const intermediateAmountOutMinWei = calculateSlippage(routeData.intermediateAmount, intermediateSlippage);
    const finalAmountOutMinWei = calculateSlippage(routeData.estimatedOutput, slippage);
    
    console.log('🔄 Atomic swap parameters:', {
      amountIn: ethers.formatUnits(amountInWei, fromToken.decimals),
      minIntermediate: ethers.formatEther(intermediateAmountOutMinWei),
      minFinalOutput: ethers.formatUnits(finalAmountOutMinWei, toToken.decimals),
    });
    
    // Execute atomic cross-DEX swap - single transaction!
    const tx = await swapperV2.swapV2CrossDex(
      routeData.dex1.router,
      routeData.dex2.router,
      amountInWei,
      intermediateAmountOutMinWei,
      finalAmountOutMinWei,
      path1,
      path2,
      deadline
    );
    
    console.log('⏳ Waiting for atomic swap confirmation:', tx.hash);
    
    return tx;
  };

  const executeV2Swap = async (signer, routeData, fromToken, toToken, fromAmount, slippage, account) => {
    const swapperV2 = new ethers.Contract(CONTRACTS.SWAPPER_V2, SWAPPER_V2_ABI, signer);
    
    const amountInWei = parseTokenAmount(fromAmount, fromToken.decimals);
    const amountOutMinWei = calculateSlippage(routeData.estimatedOutput, slippage);
    const deadline = Math.floor(Date.now() / 1000) + (20 * 60);
    
    let tx;
    
    if (fromToken.isNative) {
      // FTM to Token - call swapV2ExactETHForTokens
      tx = await swapperV2.swapV2ExactETHForTokens(
        routeData.dex.router,
        amountOutMinWei,
        routeData.path,
        deadline,
        { value: amountInWei }
      );
    } else if (toToken.isNative) {
      // Token to FTM - call swapV2ExactTokensForETH
      tx = await swapperV2.swapV2ExactTokensForETH(
        routeData.dex.router,
        amountInWei,
        amountOutMinWei,
        routeData.path,
        deadline
      );
    } else {
      // Token to Token - call swapV2ExactTokensForTokens
      tx = await swapperV2.swapV2ExactTokensForTokens(
        routeData.dex.router,
        amountInWei,
        amountOutMinWei,
        routeData.path,
        deadline
      );
    }
    
    return tx;
  };

  const executeV3Swap = async (signer, routeData, fromToken, toToken, fromAmount, slippage, account) => {
    const swapperV2 = new ethers.Contract(CONTRACTS.SWAPPER_V2, SWAPPER_V2_ABI, signer);
    
    const amountInWei = parseTokenAmount(fromAmount, fromToken.decimals);
    const amountOutMinWei = calculateSlippage(routeData.estimatedOutput, slippage);
    const deadline = Math.floor(Date.now() / 1000) + (20 * 60);
    
    // Handle native token wrapping/unwrapping for V3
    const tokenInAddress = fromToken.isNative ? TOKENS.WFTM.address : fromToken.address;
    const tokenOutAddress = toToken.isNative ? TOKENS.WFTM.address : toToken.address;
    
    // Encode V3 path
    const path = routeData.directPath 
      ? encodeV3Path([tokenInAddress, tokenOutAddress], [routeData.fee])
      : encodeV3Path(routeData.path, routeData.fees);
    
    const value = fromToken.isNative ? amountInWei : 0;
    const isNativeOut = toToken.isNative;
    
    const tx = await swapperV2.swapV3ExactInput(
      routeData.dex.router,
      path,
      amountInWei,
      amountOutMinWei,
      deadline,
      isNativeOut,
      { value }
    );
    
    return tx;
  };

  const executeSwap = useCallback(async ({
    fromToken,
    toToken,
    fromAmount,
    routeData,
    slippage,
  }) => {
    if (!web3Provider.isConnected()) {
      throw new Error('Wallet not connected');
    }

    if (!routeData) {
      throw new Error('No route data available');
    }

    setIsSwapping(true);
    setSwapError(null);

    let toastId;

    try {
      const signer = web3Provider.getSigner();
      const account = web3Provider.getAccount();

      console.log('🚀 Executing swap via SwapperV2Contract:', {
        dex: routeData.dex.name,
        type: routeData.type,
        crossDex: routeData.crossDex || false,
        from: fromToken.symbol,
        to: toToken.symbol,
        amount: fromAmount,
      });

      toastId = toast.loading('Preparing swap...');

      // Handle token approval if needed (approve to SwapperV2 contract, not router)
      if (!fromToken.isNative) {
        toast.loading('Checking token allowance...', { id: toastId });
        
        const tokenContract = new ethers.Contract(fromToken.address, ERC20_ABI, signer);
        const amountInWei = parseTokenAmount(fromAmount, fromToken.decimals);
        
        const allowance = await tokenContract.allowance(account, CONTRACTS.SWAPPER_V2);
        
        console.log('💳 Allowance check:', {
          token: fromToken.symbol,
          spender: CONTRACTS.SWAPPER_V2,
          allowance: allowance.toString(),
          needed: amountInWei.toString(),
          needsApproval: allowance < ethers.toBigInt(amountInWei),
        });

        if (allowance < ethers.toBigInt(amountInWei)) {
          toast.loading('Approving token spend...', { id: toastId });
          
          const approveTx = await tokenContract.approve(CONTRACTS.SWAPPER_V2, ethers.MaxUint256);
          
          toast.loading('Waiting for approval confirmation...', { id: toastId });
          
          try {
            await approveTx.wait();
          } catch (approvalWaitError) {
            // Handle RPC returning null receipt even though approval succeeded
            if (approvalWaitError.code === 'BAD_DATA' && approveTx.hash) {
              console.log('Approval sent but receipt returned null. Hash:', approveTx.hash);
              // Wait a bit for transaction to confirm
              await new Promise(resolve => setTimeout(resolve, 2000));
            } else {
              throw approvalWaitError;
            }
          }
        }
      }

      // Execute swap based on type via SwapperV2 contract
      let swapTx;
      
      if (routeData.crossDex) {
        // Cross-DEX swap (2 transactions)
        toast.loading(`Executing cross-DEX swap: ${routeData.dex1.name} → ${routeData.dex2.name}...`, { id: toastId });
        
        // Check if SwapperV2 is authorized in Vault
        try {
          const vaultAbi = ["function swapperContract() external view returns (address)"];
          const vault = new ethers.Contract(CONTRACTS.VAULT, vaultAbi, signer);
          const authorizedSwapper = await vault.swapperContract();
          
          console.log('🔐 Vault authorization check:', {
            vault: CONTRACTS.VAULT,
            authorizedSwapper: authorizedSwapper,
            currentSwapper: CONTRACTS.SWAPPER_V2,
            isAuthorized: authorizedSwapper.toLowerCase() === CONTRACTS.SWAPPER_V2.toLowerCase(),
          });
          
          if (authorizedSwapper.toLowerCase() !== CONTRACTS.SWAPPER_V2.toLowerCase()) {
            throw new Error(`SwapperV2 not authorized in Vault. Authorized: ${authorizedSwapper}, Current: ${CONTRACTS.SWAPPER_V2}`);
          }
        } catch (vaultCheckError) {
          console.error('❌ Vault authorization check failed:', vaultCheckError);
          throw vaultCheckError;
        }
        
        try {
          swapTx = await executeCrossDexSwap(signer, routeData, fromToken, toToken, fromAmount, slippage, account);
        } catch (crossDexError) {
          console.error('❌ Cross-DEX swap error:', crossDexError);
          throw new Error(`Cross-DEX swap failed: ${crossDexError.message || 'Unknown error'}`);
        }
      } else if (routeData.type === 'v2') {
        // Single DEX V2 swap
        toast.loading(`Executing swap on ${routeData.dex.name}...`, { id: toastId });
        swapTx = await executeV2Swap(signer, routeData, fromToken, toToken, fromAmount, slippage, account);
      } else if (routeData.type === 'v3') {
        // Single DEX V3 swap
        toast.loading(`Executing swap on ${routeData.dex.name}...`, { id: toastId });
        swapTx = await executeV3Swap(signer, routeData, fromToken, toToken, fromAmount, slippage, account);
      } else {
        throw new Error('Unknown route type');
      }

      toast.loading('Waiting for swap confirmation...', { id: toastId });
      
      let receipt;
      try {
        receipt = await swapTx.wait();
      } catch (waitError) {
        // Handle case where receipt polling fails but transaction succeeded
        if (waitError.code === 'BAD_DATA' || waitError.message?.includes('invalid value for value.index')) {
          console.log('⚠️ Receipt polling error (transaction likely succeeded), using hash:', swapTx.hash);
          receipt = { hash: swapTx.hash };
        } else {
          throw waitError;
        }
      }

      console.log('✅ Swap successful (fee automatically collected):', receipt.hash);

      // Show success message
      toast.success(
        <div>
          <div className="font-bold">Swap Successful!</div>
          <div className="text-sm">
            Swapped {fromAmount} {fromToken.symbol} for ~{routeData.estimatedOutputFormatted} {toToken.symbol}
          </div>
          <a 
            href={getTransactionUrl(receipt.hash)} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline text-sm"
          >
            View Transaction
          </a>
        </div>,
        { 
          id: toastId,
          duration: 8000,
        }
      );

      return receipt;
    } catch (error) {
      console.error('❌ Swap error:', error);
      
      let errorMessage = 'Transaction failed';
      if (error.code === 'ACTION_REJECTED') {
        errorMessage = 'Transaction rejected';
      } else if (error.message?.includes('insufficient funds')) {
        errorMessage = 'Insufficient funds';
      } else if (error.message?.includes('slippage')) {
        errorMessage = 'Price slippage too high';
      }
      
      setSwapError(errorMessage);
      toast.error(errorMessage, { id: toastId });
      throw error;
    } finally {
      setIsSwapping(false);
    }
  }, []);

  const resetSwapError = useCallback(() => {
    setSwapError(null);
  }, []);

  return {
    executeSwap,
    isSwapping,
    swapError,
    resetSwapError,
  };
};
